import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Briefcase, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ConfilexMark, ConfilexWordmark } from "@/components/ConfilexLogo";

/**
 * Login page CONFILEX — Espace personnel unifié (Option A).
 *
 * Une seule URL pour TOUS les profils (particulier / spécialiste référencé / admin).
 * Le rôle est détecté côté serveur à la connexion et l'utilisateur est
 * redirigé automatiquement vers son tableau de bord (utilisateur ou spécialiste).
 *
 * Aucune ambiguïté visuelle : un encart rassurant explique le principe avant
 * la saisie des identifiants. Vocabulaire unique : "Espace personnel".
 */
export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Redirige post-login vers la page demandée à l'origine si on a été redirigé
  // par <ProtectedRoute>. Sinon, redirige selon le rôle.
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Connexion établie. Redirection vers votre espace personnel…");
      const role = r.user.role;
      const fallback = role === "admin" ? "/admin"
                     : role === "expert" ? "/expert/dashboard"
                     : "/dashboard";
      const wantedFrom = loc.state?.from;
      nav(wantedFrom || fallback);
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B3A2A] text-white flex items-center justify-center p-6 grain relative overflow-hidden">
      <Seo
        title="Espace personnel"
        description="Connexion sécurisée à l'espace personnel CONFILEX. Utilisateurs et spécialistes référencés accèdent depuis le même formulaire — redirection automatique vers votre tableau de bord."
        canonical="/login"
        noindex
      />
      <div className="absolute inset-0 opacity-30">
        <img src="/art/sorola/promenade-1.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B3A2A]/60 to-[#0B3A2A]"></div>
      </div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10" data-testid="back-home">
          <ConfilexMark size={40} theme="light" />
          <ConfilexWordmark size={22} theme="light" />
        </Link>

        <div className="glass-dark rounded-2xl p-8 fade-up">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-2">Connexion sécurisée</p>
          <h1 className="font-serif text-3xl mb-1">Espace personnel</h1>
          <p className="text-white/60 text-sm mb-6">
            Un seul accès pour <b className="text-white">tous les profils</b>. La redirection
            vers votre tableau de bord est automatique.
          </p>

          {/* Encart "Qui suis-je ?" — supprime toute ambiguïté avant la saisie */}
          <div
            className="mb-6 p-3 rounded-xl border border-white/10 bg-white/5 grid grid-cols-2 gap-2 text-[12px]"
            data-testid="login-audience-explainer"
          >
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#C9A961]/15 flex items-center justify-center shrink-0">
                <User className="w-3.5 h-3.5 text-[#C9A961]" />
              </div>
              <div>
                <p className="text-white/85 font-medium leading-tight">Utilisateur</p>
                <p className="text-white/50 leading-tight">Dossiers, rapports, échanges spécialistes</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#C9A961]/15 flex items-center justify-center shrink-0">
                <Briefcase className="w-3.5 h-3.5 text-[#C9A961]" />
              </div>
              <div>
                <p className="text-white/85 font-medium leading-tight">Spécialiste référencé</p>
                <p className="text-white/50 leading-tight">Dossiers reçus, profil, vidéo</p>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-white/80 mb-2 block">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
                className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">Mot de passe</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={e => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
                className="bg-white/5 border-white/10 text-white rounded-xl h-11 focus:border-[#C9A961]"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold"
            >
              {loading ? "Connexion…" : "Accéder à mon espace"}
            </Button>
          </form>

          <div className="text-sm text-white/60 mt-6 flex flex-col items-center gap-2">
            <Link to="/forgot-password" className="text-[#C9A961] hover:underline" data-testid="goto-forgot-password">
              Mot de passe oublié ?
            </Link>
            <p>
              Utilisateur sans compte ?{" "}
              <Link to="/register" className="text-[#C9A961] hover:underline" data-testid="goto-register">
                Créer un compte
              </Link>
            </p>
            <p className="text-[12px] text-white/45">
              Vous êtes un professionnel ?{" "}
              <Link
                to="/register?role=expert"
                className="text-[#C9A961] hover:underline inline-flex items-center gap-0.5"
                data-testid="goto-expert-apply"
              >
                Rejoindre les spécialistes référencés <ArrowRight className="w-3 h-3" />
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
